import DOMPurify from 'isomorphic-dompurify';
import { decode } from 'he';
import { Logger } from '../../core';
import type { AllowedDomainsInTag, NextgenDOMPurifyConfig } from './types';
import { FALLBACK_DOMAIN_CONFIG } from './config';

const logger = Logger.getInstance();

const normalizeAttrValue = (value?: string | null): string =>
  value
    ?.replace(/\u200B/g, '') // remove zero-width spaces
    ?.trim()
    ?.toLowerCase() ?? '';

function decodeHtmlEntities(html: string): string {
  try {
    let decodedHtml = decode(html);

    decodedHtml = decodedHtml.replace(/class="([^"]*)"/g, (_match, classNames) => {
      const cleanedClassNames = classNames
        .split(' ')
        .map((className: string) => className.replace(/\u200B/g, ''))
        .join(' ');
      return `class="${cleanedClassNames}"`;
    });

    decodedHtml = decodedHtml.replace(/\u200B/g, '');

    return decodedHtml;
  } catch (err) {
    logger.error('Error decoding HTML entities:', err);
    return html;
  }
}

const isValidUrl = (url: string, checkSecureUrl: boolean) => {
  try {
    // Attempt to create a URL object with the given URL
    const newUrl = new URL(url);
    if (checkSecureUrl) {
      return newUrl.protocol === 'https:';
    }
    return true;
  } catch {
    // If an error occurs during URL creation, it's an invalid URL
    return false;
  }
};

const hasAcceptedUrlCriteria = (node: Element, config: AllowedDomainsInTag) => {
  const url = node.getAttribute(config.urlAttribute);
  //Return if url value does not exist
  if (!url) {
    return false;
  }
  //check if a valid url with hash
  if (url !== '#' && url.startsWith('#')) {
    return true;
  }

  // check if url is a relative path
  if (url.startsWith('/')) {
    return true;
  }

  let isValid = isValidUrl(url, config.allowOnlySecureUrls);
  //Return if its not a valid url
  if (!isValid) {
    return false;
  }
  //Check only if the there are domains in allowedDomains
  if (config.allowedDomains.length > 0) {
    isValid = config.allowedDomains.some((allowedUrl) => url.includes(allowedUrl));
  }
  return isValid;
};

const NextgenDOMPurify = (content: string, config?: NextgenDOMPurifyConfig) => {
  const decodedHtml = decodeHtmlEntities(content);
  //Use fallback in case if ALLOWED_DOMAINS_IN_ELEMENT is not defined
  const allowedDomains = config?.ALLOWED_DOMAINS_IN_ELEMENT ?? FALLBACK_DOMAIN_CONFIG;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  DOMPurify.addHook('beforeSanitizeElements', (node: Element) => {
    //Get name of node (or html tag name)
    const nodeName = node?.nodeName.toLowerCase();
    //Get allowed domain config for the node
    const allowedDomainConfig = allowedDomains.find((val) => val.tagName.toLocaleLowerCase() === nodeName);
    if (allowedDomainConfig && !hasAcceptedUrlCriteria(node, allowedDomainConfig)) {
      node.removeAttribute(allowedDomainConfig.urlAttribute);
    }
  });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'target') {
      const value = normalizeAttrValue(data.attrValue);
      if (!['_blank', '_self'].includes(value)) {
        return (data.keepAttr = false);
      }
      data.attrValue = value;
      if (value === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });
  //FORCE_BODY is required by DOMPurify to enable glue elements (like style, links etc.)
  const sanityConfig = { ...config, FORCE_BODY: true };
  const sanitizedText = DOMPurify.sanitize(decodedHtml, sanityConfig);
  DOMPurify.removeAllHooks();
  return sanitizedText;
};

export type { NextgenDOMPurifyConfig, AllowedDomainsInTag };
export { NextgenDOMPurify };
